/**
 * A modified version of ES6's native Map object, the difference being that the key has the form
 * <number,number> (hence why it's called a "Map 2D").
 *
 * This is used when you want your key to be a coordinate (useful for tables, for example) - trying
 * to do this with a normal map would force you to use strings as keys, since objects/arrays have
 * different identities
 *
 * Warning: the max value for a coordinate is `65535` due to 32-bit limitations
 * @example
 * ```
 * // Doesn't work
 * const myMap = new Map<[number,number], string>();
 * const myMap2D = new Map2D<string>();
 * myMap.set([0,0], 'foo'); // appears to work
 * myMap2D.set(0,0,'foo');
 * myMap.get([0,0]); // returns undefined instead of 'foo'
 * myMap2D.get(0,0); // returns 'foo' as expected
 * ```
 */
export class Map2D<V> {
  private _map = new Map<number, V>()
  private _valueMap = new Map<V, Set<number>>()
  set(x: number, y: number, value: V) {
    if (x > 65535 || y > 65535) {
      throw new Error(`Coordinate must fit into 16 bit precision, position "{${x},${y}}" cannot be represented`)
    } else if (Math.ceil(x) !== x && Math.ceil(y) !== y) {
      throw new Error(`Positions must be integer, "{${x},${y}}" cannot be represented`)
    } else {
      const foundValueList: Set<number> = this._valueMap.get(value) ?? new Set<number>()
      const packedCoord = (x << 16) | y
      this._valueMap.set(value, foundValueList.add(packedCoord))
      return this._map.set(packedCoord, value)
    }
  }
  get(x: number, y: number) {
    return this._map.get((x << 16) | y)
  }
  has(x: number, y: number) {
    return this._map.has((x << 16) | y)
  }
  delete(x: number, y: number) {
    const packedCoord = (x << 16) | y
    for (const [v, k] of this._valueMap.entries()) {
      k.delete(packedCoord)
      if (this._valueMap.get(v)?.size === 0) {
        this._valueMap.delete(v)
      }
    }
    return this._map.delete(packedCoord)
  }
  clear() {
    this._valueMap.clear()
    this._map.clear()
  }
  keys(): Array<[number, number]> {
    return Array.from(this._map.keys()).map((k) => [k >> 16, k & 0xffff])
  }
  values() {
    return this._map.values()
  }
  findCoordsOfValue(needle: V): [number, number][] {
    let result: [number, number][] = []
    for (const k of this._valueMap.get(needle) ?? []) {
      result.push([k >> 16, k & 0xffff])
    }
    return result
  }
  entries(): Array<[[number, number], V]> {
    let result: Array<[[number, number], V]> = []
    for (const [k, v] of this._map.entries()) {
      result.push([[k >> 16, k & 0xffff], v])
    }
    return result
  }
  forEach(cb: (val: V, key?: [number, number], map?: this) => void) {
    this._map.forEach((v, k) => cb(v, [k >> 16, k & 0xffff], this))
  }
}
